import request from '#api/axios';
import { queryKeyFactory } from '#api/queryKeyFactory';
import {
  CoachReviews,
  FindAllReviewablePtMembershipsResponseDto,
  IAverageLectureRate,
  IGetAverageReviews,
  IGetCoachReviews,
  ILectureRate,
  IPostReviewImgResponse
} from '#types/rates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

interface IPostReviewProps extends ILectureRate {
  registeredLectureId: string;
  type: 'LECTURE' | 'COACH';
  grade?: number;
  desc?: string;
}

export const postReviews = async ({
  registeredLectureId,
  type,
  coachNice,
  lectureQuality,
  timeNice,
  grade,
  desc
}: IPostReviewProps) => {
  const res = await request<string>({
    // res: rateId
    method: 'post',
    url: `/rates/registered-lectures/${registeredLectureId}`,
    data: {
      type,
      coachNice,
      lectureQuality,
      timeNice,
      grade,
      desc
    }
  });

  return res.data;
};

export const usePostReviewsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(postReviews, {
    onSuccess: () => {
      queryClient.invalidateQueries(['REVIEWS']);
    }
  });
};

interface IGetReviewsProps {
  coachId: string;
  order?: number;
}
export const getCoachReviews = async ({ coachId, order }: IGetReviewsProps) => {
  const res = await request<IGetCoachReviews>({
    method: 'get',
    url: `/rates/coach/reviews`,
    params: {
      coachId,
      order
    }
  });

  return res.data;
};

export const useGetCoachReviews = ({ coachId, order }: IGetReviewsProps) => {
  if (order) {
    return useQuery(
      queryKeyFactory.GET_REVIEWS(coachId, order),
      () => getCoachReviews({ coachId, order }),
      { enabled: !!coachId }
    );
  }
  return useQuery(
    queryKeyFactory.GET_REVIEWS(coachId),
    () => getCoachReviews({ coachId }),
    { enabled: !!coachId }
  );
};

export const getAverageReviews = async (lectureId: string) => {
  const res = await request<IGetAverageReviews>({
    method: 'get',
    url: `/rates/lecture/reviews/average/${lectureId}`
  });

  return res.data;
};

export const useGetAverageReviews = (lectureId: string) => {
  return useQuery(
    queryKeyFactory.GET_AVERAGE_REVIEWS(lectureId),
    () => getAverageReviews(lectureId),
    { enabled: !!lectureId }
  );
};

export const getCoachAverageRate = async (coachId: string) => {
  const res = await request<IAverageLectureRate>({
    method: 'get',
    url: `/rates/coach/average`,
    params: {
      coachId
    }
  });

  return res.data;
};

export const useGetCoachAverageRate = (coachId: string) => {
  return useQuery(
    queryKeyFactory.GET_COACH_AVERAGE_RATES(coachId),
    () => getCoachAverageRate(coachId),
    { enabled: !!coachId }
  );
};

export const patchReviews = async ({
  registeredLectureId,
  type,
  coachNice,
  lectureQuality,
  timeNice,
  grade,
  desc
}: Partial<IPostReviewProps>) => {
  const res = await request<string>({
    // res: rateId
    method: 'patch',
    url: `/rates/registered-lectures/${registeredLectureId}`,
    data: {
      type,
      coachNice,
      lectureQuality,
      timeNice,
      grade,
      desc
    }
  });

  return res.data;
};

export const usePatchReviews = () => {
  const queryClient = useQueryClient();
  return useMutation(patchReviews, {
    onSuccess: () => {
      queryClient.invalidateQueries(['REVIEWS']);
    }
  });
};

export const postReviewImgs = async ({
  rateId,
  payload
}: {
  rateId: string;
  payload: FormData;
}) => {
  const res = await request<IPostReviewImgResponse[]>({
    method: 'post',
    url: `/rates/${rateId}/images`,
    data: payload
  });

  return res.data;
};

export const usePostReviewImgs = () => {
  const queryClient = useQueryClient();
  return useMutation(postReviewImgs, {
    onSuccess: () => {
      queryClient.invalidateQueries(['REVIEWS']);
    }
  });
};

export const getRecentReviews = async () => {
  const res = await request<CoachReviews[]>({
    method: 'get',
    url: `/rates/recent`,
    params: {
      page: 0,
      offset: 5
    }
  });

  return res.data;
};

export const useGetRecentReviews = () => {
  return useQuery(queryKeyFactory.GET_RECENT_REVIEWS(), getRecentReviews);
};

/**
 * 리뷰 작성 요청 가능한 수강권 리스트 조회
 */
export const getRequsetableMeberships = async (personId: string) => {
  const res = await request<FindAllReviewablePtMembershipsResponseDto[]>({
    method: 'get',
    url: `/rates/${personId}/requestable-memberships`
  });

  return res.data;
};

export const useGetRequsetableMeberships = (personId: string) => {
  return useQuery(
    queryKeyFactory.GET_REQUESTABLE_MEMBERSHIPS(personId),
    () => getRequsetableMeberships(personId),
    { enabled: !!personId }
  );
};

export const postReviewRequest = async (confirmationFormId: string) => {
  const res = await request({
    method: 'post',
    url: `/rates/${confirmationFormId}/request`
  });

  return res.data;
};
